/**
 * SignupForm Component
 *
 * Multi-step signup form with OTP verification.
 * Step 1: User details form (emp_id, name, email, mobile, reason_for_request)
 * Step 2: OTP verification (6-digit code)
 * Step 3: Success message
 *
 * Pattern follows ForgotPasswordForm.tsx multi-step structure.
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { signupAPI } from '../../services/signup-api.service';
import { FormField } from '../ui/FormField';
import { Button } from '../ui/Button';
import { VALIDATION_MESSAGES } from '../../constants/validation-messages';
import { authAutoComplete } from '../../utils/auth-autocomplete';
import type {
  SignupFormData,
  SignupFormErrors,
  SignupStep,
  SignupFormProps,
} from '../../types/signup';

// OTP resend cooldown in seconds
const OTP_RESEND_COOLDOWN = 60;

export const SignupForm: React.FC<SignupFormProps> = ({ onShowToast }) => {
  const { goToLogin, validateEmail } = useAuth();

  // Multi-step state
  const [currentStep, setCurrentStep] = useState<SignupStep>('form');
  const [signupSessionId, setSignupSessionId] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState<SignupFormData>({
    emp_id: '',
    name: '',
    email: '',
    mobile: '',
    reason_for_request: '',
  });

  const [errors, setErrors] = useState<SignupFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // OTP state
  const [otp, setOtp] = useState<string>('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // Domain validation config from sessionStorage
  const emailValidationEnabled = sessionStorage.getItem('emailValidation') === 'true';
  const emailWhitelistingDomains = sessionStorage.getItem('emailwhitelisting') || '';
  const emailWhitelistingError = sessionStorage.getItem('emailwhitelistingError') || VALIDATION_MESSAGES.FORMAT.EMAIL_DOMAIN;

  // ========================================
  // Cooldown Timer Effect
  // ========================================
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Focus OTP input when step changes to OTP
  useEffect(() => {
    if (currentStep === 'otp' && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [currentStep]);

  // ========================================
  // Validation Functions
  // ========================================

  const isValidDomainEmail = (email: string): boolean => {
    if (!emailValidationEnabled || !emailWhitelistingDomains) {
      return true;
    }
    const emailDomain = email.split('@')[1];
    if (!emailDomain) return false;
    const allowedDomains = emailWhitelistingDomains.split(',').map(d => d.trim());
    return allowedDomains.includes(emailDomain);
  };

  const validateEmpId = (empId: string): string | undefined => {
    if (!empId) return VALIDATION_MESSAGES.REQUIRED.EMPLOYEE_ID;
    if (empId.length < 2) return 'Employee ID must be at least 2 characters';
    return undefined;
  };

  const validateName = (name: string): string | undefined => {
    if (!name) return VALIDATION_MESSAGES.REQUIRED.NAME;
    if (name.length < 2) return VALIDATION_MESSAGES.LENGTH.NAME_MIN;
    if (name.length > 100) return 'Name must be less than 100 characters';
    return undefined;
  };

  const validateEmailWithDomain = (email: string): string | undefined => {
    const basicEmailError = validateEmail(email);
    if (basicEmailError) return basicEmailError;
    if (emailValidationEnabled && !isValidDomainEmail(email)) {
      return emailWhitelistingError;
    }
    return undefined;
  };

  const validateMobile = (mobile: string): string | undefined => {
    if (!mobile) return VALIDATION_MESSAGES.REQUIRED.MOBILE;
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(mobile)) return VALIDATION_MESSAGES.FORMAT.MOBILE;
    return undefined;
  };

  const validateReasonForRequest = (reason: string): string | undefined => {
    if (!reason) return 'Purpose is required';
    if (reason.length < 10) return 'Purpose must be at least 10 characters';
    if (reason.length > 500) return 'Purpose must be less than 500 characters';
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: SignupFormErrors = {};

    const empIdError = validateEmpId(formData.emp_id);
    if (empIdError) newErrors.emp_id = empIdError;

    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;

    const emailError = validateEmailWithDomain(formData.email);
    if (emailError) newErrors.email = emailError;

    const mobileError = validateMobile(formData.mobile);
    if (mobileError) newErrors.mobile = mobileError;

    const reasonError = validateReasonForRequest(formData.reason_for_request);
    if (reasonError) newErrors.reason_for_request = reasonError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = (value: string): string | undefined => {
    if (!value) return 'OTP is required';
    if (!/^\d{6}$/.test(value)) return 'OTP must be 6 digits';
    return undefined;
  };

  // ========================================
  // Submit Handlers
  // ========================================

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await signupAPI.initiateSignup(formData);

      // Store session ID for OTP verification
      setSignupSessionId(response.data.signup_session_id);

      // Show success toast
      onShowToast?.(response.message || 'OTP sent to your email');

      // Start cooldown for resend
      setResendCooldown(OTP_RESEND_COOLDOWN);

      // Move to OTP step
      setCurrentStep('otp');
    } catch (error: any) {
      console.error('[SignupForm] Signup initiation failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to initiate signup. Please try again.';
      setApiError(errorMessage);
      onShowToast?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    setApiError(null);

    const otpValidationError = validateOtp(otp);
    if (otpValidationError) {
      setOtpError(otpValidationError);
      return;
    }

    setIsLoading(true);

    try {
      const response = await signupAPI.verifyOTP({
        email: formData.email,
        otp,
        signup_session_id: signupSessionId,
      });

      // Show success toast
      onShowToast?.(response.message || 'Account created successfully!');

      // Move to success step
      setCurrentStep('success');
    } catch (error: any) {
      console.error('[SignupForm] OTP verification failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Invalid OTP. Please try again.';
      setOtpError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    setOtpError(null);

    try {
      const response = await signupAPI.resendOTP({
        email: formData.email,
        signup_session_id: signupSessionId,
      });
      onShowToast?.(response.message || 'OTP resent to your email');
      setResendCooldown(OTP_RESEND_COOLDOWN);
      setOtp('');
    } catch (error: any) {
      console.error('[SignupForm] Resend OTP failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to resend OTP. Please try again.';
      setOtpError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // Event Handlers
  // ========================================

  const handleFieldChange = (field: keyof SignupFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear field-specific validation error
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    // Clear API error when user starts typing
    if (apiError) {
      setApiError(null);
    }
  };

  const handleOtpChange = (value: string) => {
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
    setOtp(digitsOnly);
    if (otpError) setOtpError(null);
  };

  const handleBackToForm = () => {
    setCurrentStep('form');
    setOtp('');
    setOtpError(null);
  };

  const handleBackToLogin = () => {
    goToLogin();
  };

  // ========================================
  // Render Functions
  // ========================================

  const renderFormStep = () => (
    <form onSubmit={handleFormSubmit} className="flex-1 flex flex-col">
      <div className="space-y-4 mt-4">
        <FormField
          label="EMPLOYEE ID"
          name="emp_id"
          type="text"
          value={formData.emp_id}
          onChange={(value) => handleFieldChange('emp_id', value)}
          error={errors.emp_id}
          placeholder="Enter your employee ID"
          required
        />

        <FormField
          label="NAME"
          name="name"
          type="text"
          value={formData.name}
          onChange={(value) => handleFieldChange('name', value)}
          error={errors.name}
          placeholder="Enter your full name"
          required
        />

        <FormField
          label="EMAIL"
          name="email"
          type="email"
          value={formData.email}
          onChange={(value) => handleFieldChange('email', value)}
          error={errors.email}
          placeholder="Enter your email address"
          autoComplete={authAutoComplete('email')}
          required
        />

        <FormField
          label="MOBILE NUMBER"
          name="mobile"
          type="tel"
          value={formData.mobile}
          onChange={(value) => handleFieldChange('mobile', value)}
          error={errors.mobile}
          placeholder="Enter 10-digit mobile number"
          maxLength={10}
          required
        />

        <div className="space-y-1">
          <label htmlFor="reason_for_request" className="block uppercase" style={{
            color: '#0F3340',
            fontFamily: 'Helvetica Neue, sans-serif',
            fontWeight: 500,
            fontSize: '1.125rem',
            lineHeight: '100%',
            letterSpacing: '0.02em'
          }}>
            PURPOSE <span className="text-red-500">*</span>
          </label>
          <textarea
            id="reason_for_request"
            name="reason_for_request"
            value={formData.reason_for_request}
            onChange={(e) => handleFieldChange('reason_for_request', e.target.value)}
            placeholder="Explain why you need access to FinPro (min 10 characters)"
            rows={3}
            className={`
              w-full px-3 py-3 rounded-lg transition-colors bg-white resize-none
              placeholder-gray-400
              ${errors.reason_for_request
                ? 'focus:outline-none focus:ring-2 focus:ring-red-500'
                : 'focus:outline-none focus:ring-2 focus:ring-[#1B84C6]'}
            `}
            style={{
              border: errors.reason_for_request ? '1px solid #fca5a5' : '1px solid #D9F0FF'
            }}
          />
          {errors.reason_for_request && (
            <p className="text-sm" style={{ color: '#CA7373' }}>{errors.reason_for_request}</p>
          )}
        </div>

        {/* API Error */}
        {apiError && (
          <div className="text-sm" style={{ color: '#CA7373' }}>
            {apiError}
          </div>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1"></div>

      {/* Buttons */}
      <div className="pb-4 space-y-3">
        <Button
          type="submit"
          loading={isLoading}
          disabled={isLoading}
          fullWidth
          size="lg"
          variant="gradient"
        >
          Continue
        </Button>

        <div className="text-center text-sm">
          <span className="font-medium" style={{ color: '#0F3340' }}>Already have an account?</span>{' '}
          <span
            onClick={handleBackToLogin}
            className="font-medium cursor-pointer hover:underline"
            style={{ color: '#0074A5' }}
          >
            Login
          </span>
        </div>
      </div>
    </form>
  );

  const renderOtpStep = () => (
    <div className="w-full h-full flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6">
        <p className="text-black font-medium">
          Please enter the 6-digit OTP sent to{" "}
          <span className="font-bold text-black">{formData.email}</span>
        </p>
      </div>

      {/* Form - Full Height */}
      <div className="flex-1 flex flex-col min-h-0">
        <form onSubmit={handleOtpSubmit} className="flex-1 flex flex-col">
          {/* Form Fields Section - Top */}
          <div className="space-y-6 mt-8">
            <div>
              <label htmlFor="otp" className="block text-lg font-bold mb-2" style={{ color: '#0F3340' }}>
                OTP
              </label>
              <input
                ref={otpInputRef}
                id="otp"
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => handleOtpChange(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength={6}
                className="w-full px-4 py-3 text-left text-lg tracking-wider rounded-lg border border-[#D9F0FF] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B84C6] focus:border-[#1B84C6]"
                autoComplete={authAutoComplete('one-time-code')}
              />
              {/* Resend Timer/Link - Below input, right-aligned */}
              <div className="mt-2 text-right text-xs">
                {resendCooldown > 0 ? (
                  <span className="text-gray-500 whitespace-nowrap">
                    Resend in {resendCooldown}s
                  </span>
                ) : (
                  <span
                    onClick={handleResendOtp}
                    className="text-black underline cursor-pointer hover:text-gray-700 whitespace-nowrap"
                  >
                    RESEND OTP
                  </span>
                )}
              </div>
            </div>

            {/* Error Message - Plain Text Below OTP Field */}
            {(otpError || apiError) && (
              <div className="text-sm" style={{ color: '#CA7373' }}>
                {otpError || apiError}
              </div>
            )}
          </div>

          {/* Spacer - pushes button to bottom */}
          <div className="flex-1"></div>

          {/* Button Section - Absolute Bottom */}
          <div className="pb-4 space-y-3">
            <Button
              type="submit"
              loading={isLoading}
              disabled={isLoading || otp.length !== 6}
              fullWidth
              size="lg"
              variant="gradient"
            >
              Verify Code
            </Button>

            <Button
              type="button"
              onClick={handleBackToForm}
              disabled={isLoading}
              fullWidth
              size="lg"
              variant="secondary"
            >
              Back
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center space-y-6 py-8">
        {/* Success Icon */}
        <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F5E9' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold" style={{ color: '#0F3340' }}>
            Request Submitted!
          </h3>
          <p className="text-gray-600 max-w-sm">
            Your account request has been submitted successfully. You will receive an email once your request is approved by an administrator.
          </p>
        </div>
      </div>

      {/* Button */}
      <div className="pb-4">
        <Button
          type="button"
          onClick={handleBackToLogin}
          fullWidth
          size="lg"
          variant="gradient"
        >
          Back to Login
        </Button>
      </div>
    </div>
  );

  // ========================================
  // Main Render
  // ========================================

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      <div className="flex-1 flex flex-col min-h-0">
        {currentStep === 'form' && renderFormStep()}
        {currentStep === 'otp' && renderOtpStep()}
        {currentStep === 'success' && renderSuccessStep()}
      </div>
    </div>
  );
};
